import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { FullscreenTakeover, QueryLoader, Button, Flex, toast } from "@jsq/ds/core";
import { useUpdateScreeningMatchesMutation } from "~/graphql/supergraphHooks";
import {
  type ProfileType,
  type ScreeningMatchType,
  type ComplianceCaseStatus,
} from "~/graphql/supergraphTypes";
import { type ProfileNameFields } from "~/pages/Compliance/utils/types";
import { getProfileNameForProfileType } from "../../Profiles/utils";
import { footerContainer_Style } from "../styles_vanilla.css";
import { ProfileWithScreening, ScreeningMatchUpdatedResults } from "../types";
import { AiScreeningProvider } from "./AiScreeningProvider";
import { AiSummaryCard } from "./AiSummaryCard";
import LexisNexisMatches from "./LexisNexisMatches";
import { ScreeningProfileView } from "./ScreeningProfileView";
import ScreeningTimeline from "./ScreeningTimeline";
import {
  FullScreenTakeOverBodyWrapper_Style,
  FullScreenTakeOverBody_Style,
  ScreeningProfile_Style,
  LexisNexisMatchesWrapper_Style,
  LexisNexisMatchesContainer_Style,
} from "./styles_vanilla.css";

const toMatchResults = (matches: ScreeningMatchType[]): ScreeningMatchUpdatedResults[] =>
  matches.map((match) => ({
    id: match.id,
    result: match.result,
    pepCategory: match.pepCategory ?? undefined,
  }));

interface Props {
  readonly: boolean;
  currentSelectedProfile?: ProfileWithScreening;
  investingProfile?: ProfileType;
  recurringHitRootProfile?: ProfileNameFields | null;
  onExit: () => void;
  queryResults?: any[];
  isPolling?: boolean;
  riskAssessmentFailed?: boolean;
  caseStatus?: ComplianceCaseStatus;
}

export const ViewScreeningDetailsFullscreenTakeover = ({
  readonly,
  currentSelectedProfile,
  investingProfile,
  recurringHitRootProfile,
  onExit,
  queryResults,
  isPolling = false,
  riskAssessmentFailed = false,
  caseStatus,
}: Props) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const profileId = searchParams.get("profileId") || "";
  const caseId = Number(location.pathname.match(/\/case\/(\d+)/)?.[1] || "");
  const positionIdForPicr = currentSelectedProfile?.positionId;

  const screeningMatches = useMemo(
    () => currentSelectedProfile?.screeningMatches || [],
    [currentSelectedProfile?.screeningMatches],
  );

  const [activeMatches, setActiveMatches] = useState<ScreeningMatchType[]>([]);

  const [updateScreeningMutation] = useUpdateScreeningMatchesMutation();

  const [matchResults, setMatchResults] = useState<ScreeningMatchUpdatedResults[]>([]);

  // Keep local edits when matches arrive/refresh after mount (e.g. screening polling).
  useEffect(() => {
    setMatchResults((prev) => {
      const prevById = new Map(prev.map((match) => [match.id, match]));
      return toMatchResults(screeningMatches).map((match) => prevById.get(match.id) ?? match);
    });
  }, [screeningMatches]);

  if (!profileId) {
    onExit();
  }

  const onSave = async () => {
    try {
      const { errors } = await updateScreeningMutation({
        awaitRefetchQueries: true,
        refetchQueries: ["GetComplianceCase"],
        variables: {
          profileId: parseInt(profileId) || -1,
          updateMatchInput: matchResults,
        },
      });
      if (errors) {
        toast.error("Failed to update screening matches");
      } else {
        toast.success("Screening matches updated successfully");
        onExit();
      }
    } catch {
      toast.error("Failed to update screening matches");
    }
  };

  const hasMatches = screeningMatches.length > 0;

  return (
    <FullscreenTakeover>
      <QueryLoader result={queryResults || []}>
        <FullscreenTakeover.Header
          onExit={onExit}
          title={`Screening - ${
            getProfileNameForProfileType(investingProfile ?? currentSelectedProfile ?? null) ||
            "--"
          }`}
        />
      </QueryLoader>
      <FullscreenTakeover.Body className={FullScreenTakeOverBodyWrapper_Style}>
        <QueryLoader result={queryResults || []}>
          <AiScreeningProvider
            currentSelectedProfile={currentSelectedProfile}
            riskAssessmentFailed={riskAssessmentFailed}
          >
            {({
              aiSummary,
              isAiLoading,
              aiProcessed,
              aiScoringResults,
              riskAssessmentFailed,
            }) => (
              <Flex className={FullScreenTakeOverBody_Style}>
                <Flex className={ScreeningProfile_Style} flexDirection="column">
                  <ScreeningProfileView
                    currentSelectedProfile={currentSelectedProfile}
                    recurringHitRootProfile={recurringHitRootProfile}
                  />
                  <ScreeningTimeline
                    caseId={caseId}
                    matches={screeningMatches}
                    setActiveMatches={setActiveMatches}
                    caseStatus={caseStatus}
                    positionId={positionIdForPicr}
                  />
                  {hasMatches && (aiSummary || isAiLoading) && !riskAssessmentFailed && (
                    <AiSummaryCard
                      summary={
                        aiSummary || {
                          totalMatches: 0,
                          highRiskCount: 0,
                          mediumRiskCount: 0,
                          lowRiskCount: 0,
                        }
                      }
                      isLoading={isAiLoading}
                      isProcessed={aiProcessed}
                      isPolling={isPolling}
                    />
                  )}
                </Flex>
                <Flex className={LexisNexisMatchesWrapper_Style}>
                  <Flex className={LexisNexisMatchesContainer_Style}>
                    <LexisNexisMatches
                      screeningMatches={activeMatches}
                      matchResults={matchResults}
                      setMatchResults={setMatchResults}
                      readonly={readonly}
                      aiScoringResults={aiScoringResults}
                      isAiLoading={isAiLoading}
                      aiProcessed={aiProcessed}
                      isPolling={isPolling}
                      riskAssessmentFailed={riskAssessmentFailed}
                    />
                  </Flex>
                </Flex>
              </Flex>
            )}
          </AiScreeningProvider>
        </QueryLoader>
      </FullscreenTakeover.Body>
      <FullscreenTakeover.Footer>
        <Flex className={footerContainer_Style} flexDirection="row-reverse" gap={8}>
          {!readonly && (
            <Button type="button" variant="primary" onClick={onSave}>
              Save
            </Button>
          )}
          <Button type="button" variant="neutral" onClick={onExit}>
            Close
          </Button>
        </Flex>
      </FullscreenTakeover.Footer>
    </FullscreenTakeover>
  );
};
